<template>
  <div>
    <div class="d-flex align-items-center pt-4">
      <div
        v-if="listing && listing.photoUrl"
        class="photo-wrapper"
      >
        <img
          ref="photo"
          class="photo"
          :src="listing.photoUrl"
        >
      </div>

      <div class="product-name">
        {{ listing.name }}
      </div>
    </div>

    <div v-if="dataPending">
      <Loader />
    </div>

    <div class="mt-4">
      <div
        v-for="(entry, index) in assignments"
        :key="index"
        class="d-flex align-items-center mb-2"
      >
        <BFormInput
          v-model="assignments[index].email"
          type="email"
          :readonly="originalAssignments.some(a => a.email === entry.email && a.roleType === entry.roleType)"
          class="field-input"
          :placeholder="$t('shop.access.emailPlaceholder')"
        />
        <select
          v-model="assignments[index].roleType"
          class="ml-2 form-control form-control-sm"
          style="width: 160px"
        >
          <option
            v-for="role in availableRoles"
            :key="role.value"
            :value="role.value"
          >
            {{ role.label }}
          </option>
        </select>
        <TooltipHelp class="ml-2">
          <div>
            <span class="font-weight-bold">{{ $t('shop.access.roles.manager') }}:</span>
            {{ $t('shop.access.roleDescriptions.manager') }}
          </div>
          <div class="mt-1">
            <span class="font-weight-bold">{{ $t('shop.access.roles.viewer') }}:</span>
            {{ $t('shop.access.roleDescriptions.viewer') }}
          </div>
        </TooltipHelp>
        <i
          class="far fa-trash text-danger ml-2"
          style="cursor: pointer; font-size: 18px;"
          @click="removeAssignment(index)"
        />
      </div>

      <button
        class="btn btn-sm btn-secondary mt-2"
        @click="addAssignment"
      >
        <i class="fas fa-plus pr-2" />
        {{ $t('shop.access.addUser') }}
      </button>
    </div>

    <hr class="mt-4">

    <div class="d-flex justify-content-end mt-4">
      <div
        v-if="error"
        class="text-danger"
      >
        {{ $t('error.error') }}
      </div>
      <div
        class="row"
        style="width: 400px; margin-left: auto; margin-right: 0"
      >
        <div class="col-6">
          <button
            class="btn btn-sm btn-block btn-secondary text-nowrap"
            @click="cancel"
          >
            <i class="fas fa-times pr-2" />
            {{ $t('general.cancel') }}
          </button>
        </div>
        <div class="col-6">
          <button
            class="btn btn-sm btn-primary text-nowrap btn-block"
            :disabled="pending || !modified"
            @click="save"
          >
            <span v-if="!pending">
              <i class="fas fa-check pr-2" />
              {{ $t('general.save') }}
            </span>
            <span v-else>
              <Loader
                color="#fff"
                size="15px"
              />
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/api/axios';
import Loader from '@/components/utils/Loader';
import { mapGetters } from 'vuex';

export default {
  props: {
    listing: Object,
  },
  data: () => ({
    assignments: [],
    originalAssignments: [],
    pending: false,
    error: false,
    dataPending: false,
  }),
  components: {
    Loader,
  },
  computed: {
    ...mapGetters('shop', ['catalogDetails']),
    modified() {
      if (this.assignments.length !== this.originalAssignments.length) return true;
      for (let i = 0; i < this.assignments.length; i += 1) {
        const a = this.assignments[i];
        const b = this.originalAssignments[i];
        if (!a || !b || a.email !== b.email || a.roleType !== b.roleType) return true;
      }
      return false;
    },
    availableRoles() {
      return [
        { value: 'ShopListingManager', label: this.$t('shop.access.roles.manager') },
        { value: 'ShopListingViewer', label: this.$t('shop.access.roles.viewer') },
      ];
    },
  },
  watch: {
    'listing.id': function listingChanged() {
      this.request();
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    request() {
      const listingId = this.listing.id;
      this.assignments = [];
      this.originalAssignments = [];
      this.error = false;
      this.dataPending = true;
      axios.get(`/shop/${this.catalogDetails.id}/listings/${listingId}/access`)
        .then(({ data }) => {
          // ignore a response for a listing that is no longer selected
          if (listingId !== this.listing.id) return;
          // data is now array of { email, roleType }
          this.assignments = data.map((x) => ({ email: x.email, roleType: x.roleType }));
          this.originalAssignments = data.map((x) => ({ email: x.email, roleType: x.roleType }));
        })
        .finally(() => {
          if (listingId === this.listing.id) this.dataPending = false;
        });
    },
    addAssignment() {
      this.assignments.push({ email: '', roleType: 'ShopListingManager' });
    },
    removeAssignment(index) {
      this.assignments.splice(index, 1);
    },
    cancel() {
      this.assignments = this.originalAssignments.map((x) => ({ ...x }));
      this.close();
    },
    save() {
      this.pending = true;
      axios.put(`/shop/${this.catalogDetails.id}/listings/${this.listing.id}/access`, {
        assignments: this.assignments,
      })
        .then(() => {
          this.originalAssignments = this.assignments.map((x) => ({ ...x }));
          this.close();
        })
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.pending = false;
        });
    },
  },
  created() {
    this.request();
  },
};
</script>

<style lang="scss" scoped>
  .product-name {
  font-size: 20px;
  font-weight: 500;
  line-height: 1.3;
}

.photo {
  max-height: 70px;
  max-width: 70px;
}

.photo-wrapper {
  width: 70px;
  height: 70px;
  min-width: 70px;
  min-height: 70px;
  border: 1px solid #efefef;
  border-radius: 5px;
  overflow: hidden;
  text-align: center;
  margin-right: 20px;
}
</style>
